import { SelectionModel } from '@angular/cdk/collections';
import { Injectable, InjectionToken } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

export interface TableSelectionHandler<T> {
  disableRowTooltip?: string;
  configure(...args: any): TableSelectionHandler<T>;

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean;

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  selectAllToggle(): void;

  selectionToggle(row): void;

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: T): string;

  isSelectionDisabled?: (row: T) => boolean;
}

export const TABLE_SELECTOR_HANDLER = new InjectionToken<TableSelectionHandler<any>>('TABLE_SELECTOR_HANDLER');

/**
 * Default service to handle table selections
 */
@Injectable()
export class DefaultTableSelectionHandler<T> implements TableSelectionHandler<T> {
  private dataSource: MatTableDataSource<T> = new MatTableDataSource([]);
  private selection: SelectionModel<T> = new SelectionModel(true, []);

  configure(dataSource: MatTableDataSource<T>, selection: SelectionModel<T>): TableSelectionHandler<T> {
    this.dataSource = dataSource;
    this.selection = selection;
    return this;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean {
    const selectedCount = this.selection?.selected?.length;
    const rowsCount = this.dataSource?.data?.length;
    return selectedCount !== null && selectedCount === rowsCount;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  selectAllToggle() {
    // clear and return if is all selected
    if (this.isAllSelected()) return this.selection.clear();
    // else
    this.selection.select(...this.dataSource.data);
  }

  selectionToggle(row: T): void {
    this.selection.toggle(row);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: T): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    const indexOfRow = this.dataSource.data.indexOf(row);
    return `${this.selection?.isSelected(row) ? 'deselect' : 'select'} row ${indexOfRow + 1}`;
  }
}
